import { useState, useEffect, useRef } from 'react';
import { Play, Pause, SkipForward, SkipBack, Maximize, X, Shield, AlertTriangle, Target, TrendingUp, CheckCircle, Zap, DollarSign } from 'lucide-react';

interface VideoReportProps {
  target: string;
  summary: any;
  findings: any[];
  businessImpact: any;
  nistMaturity: any;
  cyberRisk: any;
  benchmark: any;
  topPriorities: any[];
  attackChains: any[];
  onClose: () => void;
}

interface Slide {
  title: string;
  narration: string;
  duration: number;
  bg: string;
  render: () => JSX.Element;
}

export default function VideoReport(props: VideoReportProps): JSX.Element | null {
  const { target, summary, findings, businessImpact, nistMaturity, cyberRisk, benchmark, topPriorities, attackChains, onClose } = props;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<any>(null);
  const progressRef = useRef<any>(null);

  const criticals = findings.filter(f => f.severity === 'critical');
  const highs = findings.filter(f => f.severity === 'high');
  const score = summary?.score ?? 0;
  const grade = summary?.grade ?? '?';

  const slides: Slide[] = [
    // Slide 1: Title/Intro
    {
      title: 'Portada',
      narration: `Evaluacion de seguridad para ${target}. Informe generado automaticamente por xFreeScan.`,
      duration: 5,
      bg: 'from-purple-900 via-purple-800 to-indigo-900',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-white text-center px-12">
          <Shield className="h-16 w-16 mb-6 text-purple-300" />
          <h1 className="text-4xl font-black mb-3">Evaluacion de Seguridad</h1>
          <p className="text-2xl text-purple-200 font-bold">{target}</p>
          <div className="mt-8 flex items-center gap-6">
            <div className="text-center">
              <div className="text-5xl font-black">{grade}</div>
              <div className="text-sm text-purple-300 mt-1">Calificacion</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black">{score}<span className="text-2xl text-purple-400">/100</span></div>
              <div className="text-sm text-purple-300 mt-1">Score</div>
            </div>
          </div>
          <p className="mt-8 text-sm text-purple-400">xFreeScan by XCloud Solutions</p>
        </div>
      ),
    },
    // Slide 2: Risk Level
    {
      title: 'Nivel de Riesgo',
      narration: businessImpact ? `El nivel de riesgo organizacional es ${businessImpact.overall_risk}. ${businessImpact.ceo_message?.substring(0, 150)}` : 'Evaluando nivel de riesgo...',
      duration: 7,
      bg: score < 40 ? 'from-red-900 via-red-800 to-red-900' : score < 70 ? 'from-orange-900 via-orange-800 to-orange-900' : 'from-emerald-900 via-emerald-800 to-emerald-900',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-white text-center px-12">
          <AlertTriangle className="h-20 w-20 mb-4" />
          <h2 className="text-3xl font-black mb-2">Nivel de Riesgo</h2>
          <div className="text-6xl font-black mt-4">{businessImpact?.overall_risk || 'N/A'}</div>
          <p className="mt-6 text-lg opacity-80 max-w-2xl">{businessImpact?.ceo_message?.substring(0, 200)}</p>
        </div>
      ),
    },
    // Slide 3: Key Numbers
    {
      title: 'Hallazgos',
      narration: `Se detectaron ${findings.length} hallazgos en total: ${criticals.length} criticos, ${highs.length} de severidad alta, y ${findings.filter(f => f.severity === 'medium').length} medios.`,
      duration: 6,
      bg: 'from-gray-900 via-gray-800 to-gray-900',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-white px-12">
          <h2 className="text-3xl font-black mb-8">Hallazgos Detectados</h2>
          <div className="grid grid-cols-4 gap-8">
            {[
              { label: 'Criticos', value: criticals.length, color: 'text-red-400', bg: 'bg-red-500/20' },
              { label: 'Altos', value: highs.length, color: 'text-orange-400', bg: 'bg-orange-500/20' },
              { label: 'Medios', value: findings.filter(f => f.severity === 'medium').length, color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
              { label: 'Total', value: findings.length, color: 'text-purple-400', bg: 'bg-purple-500/20' },
            ].map((stat, i) => (
              <div key={i} className={`text-center p-6 rounded-2xl ${stat.bg}`}>
                <div className={`text-5xl font-black ${stat.color}`}>{stat.value}</div>
                <div className="text-sm mt-2 text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    // Slide 4: Top Critical Finding
    ...(criticals.length > 0 ? [{
      title: 'Hallazgo Critico #1',
      narration: `El hallazgo mas critico es: ${criticals[0]?.title}. ${criticals[0]?.description?.substring(0, 150)}`,
      duration: 8,
      bg: 'from-red-950 via-red-900 to-red-950',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-white px-16">
          <span className="px-4 py-1 rounded-full bg-red-500 text-white text-sm font-bold mb-4">CRITICO</span>
          <h2 className="text-2xl font-black text-center mb-4">{criticals[0]?.title}</h2>
          <p className="text-base text-red-200 text-center max-w-2xl">{criticals[0]?.description?.substring(0, 250)}</p>
          {criticals[0]?.evidence && (
            <div className="mt-6 p-4 bg-black/30 rounded-xl font-mono text-sm text-red-300 max-w-2xl text-center">
              {criticals[0].evidence.substring(0, 150)}
            </div>
          )}
        </div>
      ),
    }] : []),
    // Slide 5: Attack Chains
    ...(attackChains.length > 0 ? [{
      title: 'Cadenas de Ataque',
      narration: `Se identificaron ${attackChains.length} cadenas de ataque que encadenan multiples vulnerabilidades para lograr un impacto mayor.`,
      duration: 7,
      bg: 'from-orange-950 via-orange-900 to-red-950',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-white px-12">
          <Zap className="h-12 w-12 text-orange-400 mb-4" />
          <h2 className="text-3xl font-black mb-6">{attackChains.length} Cadenas de Ataque</h2>
          <div className="space-y-3 max-w-2xl w-full">
            {attackChains.slice(0, 3).map((chain: any, i: number) => (
              <div key={i} className="p-4 bg-white/10 rounded-xl">
                <p className="font-bold text-sm">{chain.title}</p>
                <p className="text-xs text-orange-300 mt-1">{chain.data_at_risk || chain.business_impact?.substring(0, 80)}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    }] : []),
    // Slide 6: Financial Impact
    ...(cyberRisk ? [{
      title: 'Riesgo Financiero',
      narration: `La probabilidad de brecha en los proximos 12 meses es del ${Math.round((cyberRisk.breach_probability_12m || 0) * 100)}%. El costo estimado seria de ${((cyberRisk.estimated_breach_cost_usd || 0) / 1000000).toFixed(1)} millones de dolares.`,
      duration: 7,
      bg: 'from-green-950 via-emerald-900 to-green-950',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-white px-12">
          <DollarSign className="h-12 w-12 text-green-400 mb-4" />
          <h2 className="text-3xl font-black mb-8">Riesgo Financiero</h2>
          <div className="grid grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white/10 rounded-2xl">
              <div className="text-4xl font-black text-red-400">{Math.round((cyberRisk.breach_probability_12m || 0) * 100)}%</div>
              <div className="text-sm text-gray-400 mt-2">Prob. Brecha 12m</div>
            </div>
            <div className="text-center p-6 bg-white/10 rounded-2xl">
              <div className="text-4xl font-black text-orange-400">${((cyberRisk.estimated_breach_cost_usd || 0) / 1000000).toFixed(1)}M</div>
              <div className="text-sm text-gray-400 mt-2">Costo Estimado</div>
            </div>
            <div className="text-center p-6 bg-white/10 rounded-2xl">
              <div className="text-4xl font-black text-green-400">{cyberRisk.roi_remediation?.roi_percentage || 0}%</div>
              <div className="text-sm text-gray-400 mt-2">ROI Remediacion</div>
            </div>
          </div>
        </div>
      ),
    }] : []),
    // Slide 7: Top Priorities
    {
      title: 'Que Hacer',
      narration: 'Estas son las acciones prioritarias que debe tomar su organizacion para mejorar la postura de seguridad.',
      duration: 8,
      bg: 'from-blue-950 via-blue-900 to-indigo-950',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-white px-12">
          <Target className="h-12 w-12 text-blue-400 mb-4" />
          <h2 className="text-3xl font-black mb-6">Top Prioridades</h2>
          <div className="space-y-3 max-w-2xl w-full">
            {topPriorities.slice(0, 4).map((p: any, i: number) => (
              <div key={i} className="flex items-center gap-4 p-3 bg-white/10 rounded-xl">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-black ${
                  p.severity === 'critical' ? 'bg-red-500' : p.severity === 'high' ? 'bg-orange-500' : 'bg-yellow-500'
                }`}>{p.rank}</div>
                <div className="flex-1">
                  <p className="font-bold text-sm">{p.title?.substring(0, 60)}</p>
                  <p className="text-xs text-blue-300">{p.timeline}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    // Slide 8: CTA / Contact
    {
      title: 'Contacto',
      narration: 'Para remediar estas vulnerabilidades, contacte a XCloud Solutions. xFreeScan, seguridad con evidencia.',
      duration: 6,
      bg: 'from-purple-900 via-purple-800 to-indigo-900',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-white text-center px-12">
          <Shield className="h-16 w-16 mb-6 text-purple-300" />
          <h2 className="text-3xl font-black mb-2">Necesita Ayuda?</h2>
          <p className="text-lg text-purple-200 mb-8">Nuestro equipo puede remediar estas vulnerabilidades</p>
          <div className="space-y-3">
            <p className="text-xl font-bold">WhatsApp: +57 316 565 0842</p>
            <p className="text-lg text-purple-300">info@xclos.com | www.xclos.com</p>
          </div>
          <p className="mt-10 text-sm text-purple-400">xFreeScan v3.0 by XCloud Solutions</p>
        </div>
      ),
    },
  ];

  // Auto-play timer
  useEffect(() => {
    if (isPlaying && currentSlide < slides.length) {
      const slide = slides[currentSlide];
      let elapsed = 0;

      progressRef.current = setInterval(() => {
        elapsed += 100;
        setProgress((elapsed / (slide.duration * 1000)) * 100);
      }, 100);

      timerRef.current = setTimeout(() => {
        if (currentSlide < slides.length - 1) {
          setCurrentSlide(prev => prev + 1);
          setProgress(0);
        } else {
          setIsPlaying(false);
        }
      }, slide.duration * 1000);
    }

    return () => {
      clearTimeout(timerRef.current);
      clearInterval(progressRef.current);
    };
  }, [currentSlide, isPlaying]);

  const toggleFullscreen = (): void => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  const currentSlideData = slides[currentSlide];
  if (!currentSlideData) return null;

  return (
    <div ref={containerRef} className={`fixed inset-0 z-[100] flex flex-col bg-black ${isFullscreen ? '' : 'p-4 sm:p-8'}`}>
      {/* Slide content */}
      <div className={`flex-1 rounded-2xl bg-gradient-to-br ${currentSlideData.bg} overflow-hidden relative transition-all duration-500`}>
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 z-10 text-white/50 hover:text-white">
          <X className="h-6 w-6" />
        </button>

        {/* Slide number */}
        <div className="absolute top-4 left-4 text-white/30 text-xs font-mono">
          {currentSlide + 1} / {slides.length}
        </div>

        {/* Slide render */}
        <div className="h-full flex items-center justify-center">
          {currentSlideData.render()}
        </div>

        {/* Narration bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm px-6 py-3">
          <p className="text-white/80 text-sm text-center">{currentSlideData.narration}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 py-3">
        <button onClick={() => { setCurrentSlide(Math.max(0, currentSlide - 1)); setProgress(0); }} className="text-white/60 hover:text-white">
          <SkipBack className="h-5 w-5" />
        </button>
        <button onClick={() => setIsPlaying(!isPlaying)} className="h-10 w-10 rounded-full bg-purple-600 flex items-center justify-center text-white hover:bg-purple-500">
          {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </button>
        <button onClick={() => { setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1)); setProgress(0); }} className="text-white/60 hover:text-white">
          <SkipForward className="h-5 w-5" />
        </button>
        <button onClick={toggleFullscreen} className="text-white/60 hover:text-white ml-4">
          <Maximize className="h-5 w-5" />
        </button>

        {/* Progress bar */}
        <div className="flex-1 max-w-xs h-1 bg-white/20 rounded-full overflow-hidden ml-4">
          <div className="h-full bg-purple-500 transition-all duration-100" style={{ width: `${progress}%` }} />
        </div>

        {/* Slide dots */}
        <div className="flex gap-1 ml-4">
          {slides.map((_, i) => (
            <button key={i} onClick={() => { setCurrentSlide(i); setProgress(0); }}
              className={`h-2 rounded-full transition-all ${i === currentSlide ? 'w-6 bg-purple-500' : 'w-2 bg-white/30 hover:bg-white/50'}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
